import {
  forwardRef,
  useCallback,
  useEffect,
  useImperativeHandle,
  useMemo,
  useState,
} from 'react';
import { useTranslation } from 'react-i18next';
import { Controller, useForm } from 'react-hook-form';
import parseMax from 'libphonenumber-js/max';
import Skeleton from 'react-loading-skeleton';

import {
  Dropdown,
  FormInput,
  PhoneFormInput,
  restCountries,
} from '@unified-checkout/kit-components';
import {
  ContactFormAddress,
  ContactFormValues,
  FlattenedContactFormValues,
  IContactFormProps,
} from 'libs/kit-components/src/components/ContactForm';
import {
  FIELD_TYPES,
  flattenContactFormValues,
  getAdditionalFields,
  getCountryFromPhoneNumber,
} from 'libs/kit-components/src/components/ContactForm/utils';
export type RestCountryType = Partial<(typeof restCountries)[0]>;

const ADDRESS_TYPES = { SHIPPING: 'shipping', BILLING: 'billing' } as const;
type AddressType = (typeof ADDRESS_TYPES)[keyof typeof ADDRESS_TYPES];

export const ContactFormV2 = forwardRef((props: IContactFormProps, ref) => {
  const {
    onClientData,
    collectShippingAddress,
    collectBillingAddress,
    phoneRequired = true,
    nameRequired = true,
    defaultCountryISO = 'EG',
    defaultValues,
    onValidityChange,
    disableFields,
    disableDropdown,
    verifiedFields,
    allowedCountries,
    sortByAllowedCountries,
    mode = 'onChange',
  } = props;

  const {
    register,
    watch,
    formState: { errors, isDirty, isValid, dirtyFields },
    setValue,
    trigger,
    clearErrors,
    control,
    getValues,
  } = useForm({
    mode,
    shouldUnregister: true,
    defaultValues: flattenContactFormValues(defaultCountryISO, defaultValues),
  });
  const countriesSource = useMemo(() => {
    if (allowedCountries?.length) {
      return restCountries.filter((country) =>
        allowedCountries.includes(country.iso)
      );
    }
    return restCountries;
  }, [allowedCountries]);

  const {
    defaultCountry,
    defaultShippingCountry,
    defaultBillingCountry,
    defaultShippingPhoneCountry,
    defaultBillingPhoneCountry,
  } = useMemo(() => {
    const fallback =
      countriesSource.find((country) => country.iso === defaultCountryISO) ||
      countriesSource[0] ||
      null;

    const shippingPhoneCountry = getCountryFromPhoneNumber(
      defaultValues?.shipping?.phone_number
    );
    const billingPhoneCountry = getCountryFromPhoneNumber(
      defaultValues?.billing?.phone_number
    );

    const shippingAddressCountry = defaultValues?.shipping?.country
      ? countriesSource.find(
          (country) => country.iso === defaultValues.shipping?.country
        ) || null
      : null;

    const billingAddressCountry = defaultValues?.billing?.country
      ? countriesSource.find(
          (country) => country.iso === defaultValues.billing?.country
        ) || null
      : null;

    return {
      defaultCountry: fallback,
      defaultShippingCountry:
        shippingAddressCountry || shippingPhoneCountry || fallback,
      defaultBillingCountry:
        billingAddressCountry || billingPhoneCountry || fallback,
      defaultShippingPhoneCountry:
        shippingPhoneCountry || shippingAddressCountry || fallback,
      defaultBillingPhoneCountry:
        billingPhoneCountry || billingAddressCountry || fallback,
    };
  }, [countriesSource, defaultCountryISO, JSON.stringify(defaultValues)]);

  const { t } = useTranslation('kitComponents');
  const [country, setCountry] = useState<RestCountryType | null>(
    defaultCountry
  );
  const [shippingCountry, setShippingCountry] = useState<RestCountryType>(
    defaultShippingCountry!
  );
  const [shippingPhoneCountry, setShippingPhoneCountry] =
    useState<RestCountryType>(defaultShippingPhoneCountry!);
  const [billingCountry, setBillingCountry] = useState<RestCountryType>(
    defaultBillingCountry!
  );
  const [billingPhoneCountry, setBillingPhoneCountry] =
    useState<RestCountryType>(defaultBillingPhoneCountry!);
  const [regionData, setRegionData] = useState<any>(null);
  const [isLoadingRegionData, setIsLoadingRegionData] = useState(false);

  const countriesOptions = useMemo(() => {
    let filteredCountries = restCountries;
    if (allowedCountries?.length) {
      filteredCountries = restCountries.filter((country) =>
        allowedCountries.includes(country.iso)
      );

      if (sortByAllowedCountries) {
        filteredCountries = [...filteredCountries].sort((a, b) => {
          return (
            allowedCountries.indexOf(a.iso) - allowedCountries.indexOf(b.iso)
          );
        });
      }
    }

    return filteredCountries.map((country) => ({
      label: country.name,
      value: country.iso,
      icon: country.icon,
      code: country.code,
    }));
  }, []);

  const watchFields = watch();

  useImperativeHandle(ref, () => ({
    getIsValid: () => isValid,
    trigger: () => trigger(),
    getFormFields: () => ({ ...watchFields, country }),
  }));

  const watchFieldsJson = useMemo(
    () => JSON.stringify(watchFields),
    [watchFields]
  );

  useEffect(() => {
    if (onClientData && isValid && (isDirty || defaultValues)) {
      const data: Partial<ContactFormValues> = {};

      for (const key in watchFields) {
        const value = watchFields[key as keyof typeof watchFields];
        if (key.startsWith('shipping_')) {
          const field = key.replace(
            'shipping_',
            ''
          ) as keyof ContactFormAddress;
          if (!data.shipping) data.shipping = {} as ContactFormAddress;
          data.shipping![field as keyof ContactFormAddress] = value as string;
          if (field === 'phone_number') {
            data.shipping.phone_number = shippingPhoneCountry?.code! + value;
          }
        } else if (key.startsWith('billing_')) {
          const field = key.replace('billing_', '') as keyof ContactFormAddress;
          if (!data.billing) data.billing = {} as ContactFormAddress;
          data.billing![field as keyof ContactFormAddress] = value as string;
          if (field === 'phone_number') {
            data.billing.phone_number = billingPhoneCountry?.code! + value;
          }
        } else {
          (data as any)[key] = value;
          if (key === 'phone') {
            data.phone = country?.code! + value;
          }
        }
      }

      onClientData(data);
    } else if (onClientData) {
      onClientData(null);
    }

    const checkIsChanged = () => {
      const currentValues = getValues();
      let isChanged = false;

      for (const key in currentValues) {
        let currentFieldValue =
          currentValues[key as keyof typeof currentValues];
        let defaultValue = defaultValues?.[key as keyof typeof defaultValues];

        if (
          typeof currentFieldValue === 'string' &&
          typeof defaultValue === 'string'
        ) {
          currentFieldValue = currentFieldValue.trimEnd();
          defaultValue = defaultValue.trimEnd();
        }
        if (
          currentFieldValue !== defaultValue &&
          dirtyFields[key as keyof typeof currentValues]
        ) {
          return (isChanged = true);
        }
      }
      return isChanged;
    };

    onValidityChange?.(
      isValid && (isDirty || !!defaultValues),
      (!verifiedFields?.email && !!getValues('email')) ||
        (!verifiedFields?.phone && !!getValues('phone')) ||
        !!checkIsChanged()
    );
  }, [isDirty, isValid, watchFieldsJson, Object.keys(dirtyFields).length]);

  useEffect(() => {
    const getRegionData = async () => {
      setIsLoadingRegionData(true);
      try {
        const data = await import('./countries-states-cities.json');
        setRegionData(data.default);
      } finally {
        setIsLoadingRegionData(false);
      }
    };

    if (collectBillingAddress || collectShippingAddress) {
      getRegionData();
    }
  }, []);

  useEffect(() => {
    if (
      collectShippingAddress &&
      (dirtyFields.shipping_phone_number || getValues('shipping_phone_number'))
    ) {
      trigger('shipping_phone_number');
    }
  }, [shippingPhoneCountry?.code]);

  useEffect(() => {
    if (
      collectBillingAddress &&
      (dirtyFields.billing_phone_number || getValues('billing_phone_number'))
    ) {
      trigger('billing_phone_number');
    }
  }, [billingPhoneCountry?.code]);

  const createFullNameRegister = useCallback(
    ({ field = 'name' }: { field?: keyof FlattenedContactFormValues } = {}) => {
      return register(field, {
        onChange: (e) => {
          clearErrors(field);
          if (!e.target.value) {
            setValue(field, '', { shouldValidate: false });
          } else {
            const name = e.target.value;
            const nameRegex = /^((?:[A-Za-z]+ ?){1,3})$/;
            const displayValue = name
              .replace(/[^a-zA-Z ]/g, '')
              .replace(/\s+/g, ' ');
            if (nameRegex.test(displayValue)) {
              setValue(field, displayValue.toUpperCase());
            } else {
              setValue(field, displayValue.trim().toUpperCase());
            }
          }
        },
        required: nameRequired
          ? {
              value: true,
              message: t('NAME_IS_REQUIRED'),
            }
          : false,
      });
    },
    []
  );

  const createPhoneRegister = useCallback(
    ({
      field = 'phone',
      countryCode,
    }: {
      field?: keyof FlattenedContactFormValues;
      countryCode: string;
    }) => {
      return register(field, {
        onChange: (e) => {
          clearErrors(field);
          const phoneNumber = e.target.value;
          const trimmedNumber = phoneNumber.replace(/\D/g, '');
          setValue(field, trimmedNumber);
        },
        required: phoneRequired
          ? {
              value: true,
              message: t('PHONE_IS_REQUIRED'),
            }
          : false,
        validate: (value) => {
          if (!value) return !phoneRequired || t('PHONE_IS_REQUIRED');
          const phone = parseMax(countryCode + value);
          const isValidMobile =
            phone?.isValid() && (phone?.getType() === 'MOBILE' || phone?.getType() === 'FIXED_LINE_OR_MOBILE');
          return (
            isValidMobile || t('PHONE_NOT_VALID')
          );
        },
      });
    },
    []
  );

  const getInputClasses = (fieldName: keyof FlattenedContactFormValues) => {
    const hasError = errors[fieldName];

    let classes = 'english-label-regular !border rounded-lg ';

    if (hasError) {
      classes += '!z-10 !border-red-300 ';
    } else {
      classes += '!border-gray-300 ';
    }

    return classes;
  };

  const handleCountryChange = (
    country: (typeof countriesOptions)[0],
    addressType: AddressType
  ) => {
    const { code, label, value } = country;
    const updatedCountry = { name: label, iso: value, code };

    if (addressType === ADDRESS_TYPES.SHIPPING) {
      setShippingCountry(updatedCountry);
    } else {
      setBillingCountry(updatedCountry);
    }
  };

  const renderAddressSection = (prefix: AddressType) => {
    const countryField = `${prefix}_country` as const;
    const country =
      (prefix === ADDRESS_TYPES.SHIPPING ? shippingCountry : billingCountry) ||
      ({} as RestCountryType);
    const countryISO = country.iso!;
    const additionalFields = getAdditionalFields(countryISO);
    return (
      <div className='[&_label]:english-utility-label-medium [&_label]:!text-gray-700'>
        <div className="grid grid-cols-2 gap-x-11 gap-y-3">
          <div className="space-y-2">
            <label htmlFor={`${prefix}_full_name`}>Full name</label>
            <FormInput
              formRegister={createFullNameRegister({
                field: `${prefix}_full_name`,
              })}
              name={`${prefix}_full_name`}
              error={errors[`${prefix}_full_name`]}
              className={getInputClasses(`${prefix}_full_name`)}
              placeholder="ex. Abdullah Adel"
            />
            {errors[`${prefix}_full_name`] && <p className="text-red-600 english-helper-text-regular">{errors[`${prefix}_full_name`]?.message}</p>}
          </div>
          <div className="space-y-2">
            <label htmlFor={`${prefix}_phone_number`}>Phone number</label>
            <PhoneFormInput
              name={`${prefix}_phone_number`}
              error={errors[`${prefix}_phone_number`]}
              selectedCountry={{
                name:
                  prefix === ADDRESS_TYPES.SHIPPING
                    ? shippingPhoneCountry.name!
                    : billingPhoneCountry.name!,
                code:
                  prefix === ADDRESS_TYPES.SHIPPING
                    ? shippingPhoneCountry.code!
                    : billingPhoneCountry.code!,
              }}
              className={getInputClasses(`${prefix}_phone_number`)}
              setSelectedCountry={(country: any) =>
                prefix === ADDRESS_TYPES.SHIPPING
                  ? setShippingPhoneCountry(country)
                  : setBillingPhoneCountry(country)
              }
              phoneFormRegister={createPhoneRegister({
                field: `${prefix}_phone_number`,
                countryCode:
                  prefix === ADDRESS_TYPES.SHIPPING
                    ? shippingPhoneCountry.code!
                    : billingPhoneCountry.code!,
              })}
              placeholder="10 000 000 00"
              allowedCountries={allowedCountries}
              sortByAllowedCountries
              disableDropdown={disableDropdown?.phone}
              disabled={disableFields?.phone}
            />
            {errors[`${prefix}_phone_number`] && <p className="text-red-600 english-helper-text-regular">{errors[`${prefix}_phone_number`]?.message}</p>}
          </div>
          <div className="space-y-2">
            <label htmlFor={countryField}>Country</label>
            <Controller
              name={countryField}
              control={control}
              defaultValue={country?.iso}
              rules={{ required: t('COUNTRY_IS_REQUIRED') }}
              render={({ field: { onChange, value } }) => (
                <Dropdown
                  label={"Select Country"}
                  onChange={(country: (typeof countriesOptions)[0]) => {
                    if (country.value !== value) {
                      onChange(country.value);
                      additionalFields?.forEach(({ field }) => {
                        const fieldName =
                          `${prefix}_${field}` as keyof FlattenedContactFormValues;
                        setValue(fieldName, '', { shouldValidate: false });
                      });
                      handleCountryChange(country, prefix);
                    }
                  }}
                  options={countriesOptions}
                  defaultOption={country?.iso || ''}
                  className={`${getInputClasses(`${prefix}_country`)}`}
                  hideCircleIcon
                  hideRemoveOptionBtn
                />
              )}
            />
          </div>
          <div className="space-y-2">
            <label htmlFor={`${prefix}_address_line_1`}>Address line 1</label>

            <FormInput
              formRegister={register(`${prefix}_address_line_1`, {
                required: {
                  value: true,
                  message: t('ADDRESS_IS_REQUIRED'),
                },
              })}
              name={`${prefix}_address_line_1`}
              error={errors[`${prefix}_address_line_1`]}
              placeholder="ex. 123 Street, Maadi"
              className={getInputClasses(`${prefix}_address_line_1`)}
            />
            {errors[`${prefix}_address_line_1`] && <p className="text-red-600 english-helper-text-regular">{errors[`${prefix}_address_line_1`]?.message}</p>}
          </div>
          <div className="space-y-2">
            <label htmlFor={`${prefix}_address_line_2`}>
              Address line 2{' '}
              <span className="text-gray-500 english-utility-label-compact-regular">
                (optional)
              </span>
            </label>
            <FormInput
              formRegister={register(`${prefix}_address_line_2`)}
              name={`${prefix}_address_line_2`}
              placeholder="ex. 123 Street, Maadi"
              className={getInputClasses(`${prefix}_address_line_2`)}
            />
            {errors[`${prefix}_address_line_2`] && <p className="text-red-600 english-helper-text-regular">{errors[`${prefix}_address_line_2`]?.message}</p>}
          </div>
          {additionalFields.map(({ name, field, type, isRequired, placeholder }, idx) => {
            const label = field.includes('postal_code')
              ? 'PIN Code'
              : name.charAt(0).toUpperCase() + name.slice(1);
            const fieldName =
              `${prefix}_${field}` as keyof FlattenedContactFormValues;

            if (type === FIELD_TYPES.TEXT) {
              return (
                <div className="space-y-2">
                  <label htmlFor={fieldName}>{label}</label>
                  <FormInput
                    key={field}
                    formRegister={register(fieldName, {
                      required: isRequired
                        ? t(`${field.toUpperCase()}_IS_REQUIRED`)
                        : false,
                    })}
                    name={fieldName}
                    error={errors[fieldName]}
                    placeholder={placeholder}
                    className={getInputClasses(fieldName)}
                  />
                  {errors[fieldName] && <p className="text-red-600 english-helper-text-regular">{errors[fieldName]?.message}</p>}
                </div>
              );
            } else {
              if (isLoadingRegionData) {
                return (
                  <div key={field} className="pb-1">
                    <Skeleton height={40} />
                  </div>
                );
              }

              if (!regionData || !countryISO) return null;

              const statesList = Object.keys(regionData[countryISO].states).map(
                (state) => ({ label: state, value: state })
              );
              const selectedCountryState = watchFields[`${prefix}_state`]!;
              const citiesList = regionData[countryISO].states[
                selectedCountryState
              ]?.cities.map((city: string) => ({ label: city, value: city }));

              const options = field === 'state' ? statesList : citiesList;

              if (!options?.length) return null;

              const key =
                field === 'state'
                  ? countryISO + field
                  : countryISO + selectedCountryState + field;

              return (
                <div className="space-y-2">
                  <label htmlFor={fieldName}>{label}</label>

                  <Controller
                    key={key}
                    name={fieldName}  
                    control={control}
                    rules={{
                      required:
                        isRequired && t(`${name.toUpperCase()}_IS_REQUIRED`),
                    }}
                    render={({ field: { onChange, value } }) => (
                      <Dropdown
                        label={placeholder}
                        onChange={(selected: any) => {
                          if (selected.value !== value) {
                            onChange(selected.value);
                            const fieldName =
                              `${prefix}_${field}` as keyof FlattenedContactFormValues;
                            if (fieldName.includes('state')) {
                              setValue(`${prefix}_city`, '');
                              setValue(`${prefix}_postal_code`, '');
                            } else if (fieldName.includes('city')) {
                              setValue(`${prefix}_postal_code`, '');
                            }
                          }
                        }}
                        options={options}
                        className={`${getInputClasses(fieldName)}`}
                        hideCircleIcon
                        hideRemoveOptionBtn
                        defaultOption={value as string}
                      />
                    )}
                  />
                </div>
              );
            }
          })}
        </div>
      </div>
    );
  };
  return (
    <form className="flex flex-col gap-4">
      {collectShippingAddress && renderAddressSection(ADDRESS_TYPES.SHIPPING)}
      {collectBillingAddress && renderAddressSection(ADDRESS_TYPES.BILLING)}
    </form>
  );
});
